'use client';
import React from 'react';
import '../../../assests/css/otp.css';
import { useEffect, useState, useRef } from 'react';
import { AppDispatch } from '../../../store/index';
import { useDispatch } from 'react-redux';
import { otpVerification } from '../../../slices/authSlice';
import { AuthResponse, Otp } from '../../../dtos/auth';
import Cookies from 'universal-cookie';
import { getAllModuleTrigger } from '../../../slices/moduleSlice';
import { useNavigate } from 'react-router-dom';
import InlineSpinner from '../../Layout/InlineSpinner';
import Form from '../../Layout/Form';

const OtpComponent = (userDetails: { email: string; token: string | undefined }): React.JSX.Element => {
  const [email, setEmail] = useState('');
  const dispatch = useDispatch<AppDispatch>();
  const cookies = new Cookies(null, { path: '/' });
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);

  const [formData, setFormData] = useState<Otp>({
    first: '',
    second: '',
    third: '',
    fourth: '',
    fifth: '',
    sixth: '',
    otp: '',
    token: ''
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleFormFieldChange = (fieldName: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));
    
    // Clear validation error for this field
    if (formErrors[fieldName]) {
      setFormErrors(prev => {
        const { [fieldName]: removed, ...rest } = prev;
        return rest;
      });
    }
  };

  const firstInputRef = useRef<HTMLInputElement | null>(null);
  const secondInputRef = useRef<HTMLInputElement | null>(null);
  const thirdInputRef = useRef<HTMLInputElement | null>(null);
  const fourthInputRef = useRef<HTMLInputElement | null>(null);
  const fifthInputRef = useRef<HTMLInputElement | null>(null);
  const sixthInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (localStorage.getItem('otpVerified') === 'true') {
      navigate('/home', { replace: true });
    }
    setTimeout(() => {
      setLoading(false);
    }, 2000);
  }, []);

  useEffect(() => {
    window.history.pushState(null, '', window.location.href);
    window.onpopstate = () => {
      window.history.pushState(null, '', window.location.href);
    };
  }, []);

  useEffect(() => {
    console.log(userDetails);

    const hideEmail = function (email: string) {
      return email.replace(/(.{2})(.*)(?=@)/, function (gp1, gp2, gp3) {
        for (let index = 0; index < gp3.length; index++) {
          gp2 += '*';
        }
        return gp2;
      });
    };

    if (userDetails.email) {
      setEmail(hideEmail(userDetails.email));
    }
  }, [userDetails]);

  const handleLogout = () => {
    navigate('/');
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    nextInput: HTMLInputElement | null,
  ) => {
    const { value, name } = e.target;
    handleFormFieldChange(name, value); // Update form state

    if (value.length === 1 && nextInput) {
      nextInput.focus();
    }
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    prevInput: HTMLInputElement | null,
  ) => {
    if (e.key === 'Backspace' && !e.currentTarget.value && prevInput) {
      prevInput.focus();
    }
  };

  const onSubmit = async (data: Record<string, any>) => {
    const otpCode = 
      (data.first || '') +
      (data.second || '') +
      (data.third || '') +
      (data.fourth || '') +
      (data.fifth || '') +
      (data.sixth || '');
    
    if (!otpCode || otpCode.length < 6) {
      console.log('Please enter a 6-digit code');
      return;
    }

    setIsSubmitting(true);
    const toastId = console.log('Processing...');
    
    try {
      const otpData: Otp = {
        first: data.first || '',
        second: data.second || '',
        third: data.third || '',
        fourth: data.fourth || '',
        fifth: data.fifth || '',
        sixth: data.sixth || '',
        otp: otpCode,
        token: userDetails.token || ''
      };

      const response = await dispatch(otpVerification(otpData)).unwrap();

      if (response.data) {
        console.log('Login Successfully');
        cookies.remove('otp');
        cookies.set(
          'userDetails',
          JSON.stringify({ accessToken: response.data.accessToken, user: response.data.user }),
        );
        localStorage.setItem('otpVerified', 'true');
        dispatch(getAllModuleTrigger());
        setTimeout(() => {
          navigate('/home');
        }, 3000);
      } else {
        console.log('Error:', response.message);
      }
    } catch (error) {
      console.log('An error occurred during OTP verification');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Form schema for OTP inputs
  const formSchema = [
    {
      name: 'otp',
      type: 'custom' as const,
      label: 'Enter OTP',
      customComponent: () => (
        <div className="otp-inputs d-flex gap-2 justify-content-center">
          <input
            ref={firstInputRef}
            type="text"
            name="first"
            maxLength={1}
            className="otp-input"
            value={formData.first}
            onChange={(e) => handleInputChange(e, secondInputRef.current)}
            onKeyDown={(e) => handleKeyDown(e, null)}
          />
          <input
            ref={secondInputRef}
            type="text"
            name="second"
            maxLength={1}
            className="otp-input"
            value={formData.second}
            onChange={(e) => handleInputChange(e, thirdInputRef.current)}
            onKeyDown={(e) => handleKeyDown(e, firstInputRef.current)}
          />
          <input
            ref={thirdInputRef}
            type="text"
            name="third"
            maxLength={1}
            className="otp-input"
            value={formData.third}
            onChange={(e) => handleInputChange(e, fourthInputRef.current)}
            onKeyDown={(e) => handleKeyDown(e, secondInputRef.current)}
          />
          <input
            ref={fourthInputRef}
            type="text"
            name="fourth"
            maxLength={1}
            className="otp-input"
            value={formData.fourth}
            onChange={(e) => handleInputChange(e, fifthInputRef.current)}
            onKeyDown={(e) => handleKeyDown(e, thirdInputRef.current)}
          />
          <input
            ref={fifthInputRef}
            type="text"
            name="fifth"
            maxLength={1}
            className="otp-input"
            value={formData.fifth}
            onChange={(e) => handleInputChange(e, sixthInputRef.current)}
            onKeyDown={(e) => handleKeyDown(e, fourthInputRef.current)}
          />
          <input
            ref={sixthInputRef}
            type="text"
            name="sixth"
            maxLength={1}
            className="otp-input"
            value={formData.sixth}
            onChange={(e) => handleInputChange(e, null)}
            onKeyDown={(e) => handleKeyDown(e, fifthInputRef.current)}
          />
        </div>
      )
    }
  ];

  return (
    <div className="main-inner-section d-flex align-items-center justify-content-center flex-column">
      {loading ? (
        <div className="d-flex justify-content-center">
          <InlineSpinner size="medium" />
        </div>
      ) : (
        <div className="container height-100 d-flex justify-content-center align-items-center">
          <div className="position-relative">
            <div className="card otp-card p-5 text-center">
              <div>
                <h3 className="text-center otp-heading">OTP</h3>
                <h4>Please enter the code to verify your account</h4>
                <div className="py-3">
                  {' '}
                  <h6>
                    A code has been sent to <b>{email} </b>
                  </h6>{' '}
                </div>
                <Form
                  schema={formSchema}
                  initialData={formData}
                  onSubmit={onSubmit}
                  onFieldChange={handleFormFieldChange}
                  validationErrors={formErrors}
                  isLoading={isSubmitting}
                  submitButtonText="Validate"
                  submitButtonProps={{ className: "validate" }}
                />
                <div className="mt-4 d-flex align-items-center justify-content-center">
                  <button type="button" className="nav-btn" onClick={handleLogout}>
                    Back to login
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OtpComponent;
